import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useAuthSetup } from "@/hooks/useAuthSetup";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Package,
  MapPin,
  Truck,
  AlertCircle,
  CheckCircle2,
  Clock,
  BoxIcon,
  ArrowRight,
  User,
  Building2,
  QrCode,
} from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/_public/track/$code")({
  component: TrackingPage,
});

const STATUS_LABELS: Record<string, string> = {
  box_delivered: "Caja Entregada",
  received: "Recibido",
  consolidated: "Consolidado",
  in_transit: "En Tránsito",
  delivered: "Entregado",
  returned: "Devuelto",
};

const STATUS_COLORS: Record<string, string> = {
  box_delivered: "bg-amber-100 text-amber-800 border-amber-200",
  received: "bg-blue-100 text-blue-800 border-blue-200",
  consolidated: "bg-purple-100 text-purple-800 border-purple-200",
  in_transit: "bg-cyan-100 text-cyan-800 border-cyan-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
  returned: "bg-red-100 text-red-800 border-red-200",
};

const EVENT_LABELS: Record<string, string> = {
  box_delivered: "Caja entregada al cliente",
  received: "Paquete recibido en bodega",
  consolidated: "Agregado a consolidado",
  in_transit: "En camino",
  delivered: "Entregado",
  returned: "Devuelto",
};

const EVENT_ICONS: Record<string, React.ReactNode> = {
  box_delivered: <BoxIcon className="h-4 w-4" />,
  received: <Package className="h-4 w-4" />,
  consolidated: <Truck className="h-4 w-4" />,
  in_transit: <Truck className="h-4 w-4" />,
  delivered: <CheckCircle2 className="h-4 w-4" />,
  returned: <AlertCircle className="h-4 w-4" />,
};

function TrackingPage() {
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthSetup();

  const trackingData = useQuery(
    api.companies.logistics.packages.queries.getPublicTrackingByCode,
    { code },
  );

  useEffect(() => {
    if (
      isAuthenticated &&
      trackingData?.type === "package" &&
      trackingData.packageId
    ) {
      navigate({
        to: "/logistics/packages/$id",
        params: { id: trackingData.packageId },
      });
    }
  }, [isAuthenticated, trackingData, navigate]);

  if (trackingData === undefined) {
    return <LoadingState />;
  }

  if (trackingData.type === "not_found") {
    return <NotFoundState code={code} />;
  }

  if (trackingData.type === "verification") {
    return (
      <VerificationView data={trackingData} isAuthenticated={isAuthenticated} />
    );
  }

  return <PackageTrackingView data={trackingData} />;
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <Package className="h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Buscando información...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NotFoundState({ code }: { code: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">No encontrado</h2>
          <p className="text-muted-foreground text-center mb-4">
            No se encontró ningún paquete con el código proporcionado.
          </p>
          <div className="bg-muted rounded-md px-4 py-2">
            <p className="text-sm font-mono">{code}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function VerificationView({
  data,
  isAuthenticated,
}: {
  data: {
    type: "verification";
    code: string;
    packageId: string;
    orderNumber: string;
    status: string;
    companyName: string | null;
    isBoxDelivered: boolean;
  };
  isAuthenticated: boolean;
}) {
  const navigate = useNavigate();

  const handleGoToPickup = () => {
    if (isAuthenticated) {
      navigate({
        to: "/logistics/packages/$id",
        params: { id: data.packageId },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <QrCode className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-2xl">Código de Verificación</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">Orden</p>
            <p className="text-xl font-mono font-semibold">
              {data.orderNumber}
            </p>
          </div>

          {data.companyName && (
            <>
              <Separator />
              <div className="flex items-center gap-3 justify-center">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{data.companyName}</span>
              </div>
            </>
          )}

          <Separator />

          {data.isBoxDelivered ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <BoxIcon className="h-8 w-8 text-amber-600 mx-auto mb-2" />
              <p className="font-medium text-amber-800 mb-1">
                Caja pendiente de recogida
              </p>
              <p className="text-sm text-amber-700">
                Este código se usa para verificar la recogida de la caja.
              </p>
              {isAuthenticated && (
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={handleGoToPickup}
                >
                  Ir a registrar recogida
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              {!isAuthenticated && (
                <p className="text-xs text-muted-foreground mt-4">
                  Inicia sesión para registrar la recogida.
                </p>
              )}
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium text-green-800">Caja ya recogida</p>
              <p className="text-sm text-green-700">
                El contenido de esta caja ya fue procesado.
              </p>
            </div>
          )}

          <Separator />

          <p className="text-xs text-center text-muted-foreground font-mono">
            {data.code}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function PackageTrackingView({
  data,
}: {
  data: {
    type: "package";
    code: string;
    packageId: string;
    orderNumber: string;
    trackingNumber: string | null;
    status: string;
    companyName: string | null;
    senderName: string | null;
    senderCity: string | null;
    recipientName: string | null;
    recipientCity: string | null;
    shipmentName: string | null;
    carrierName: string | null;
    timeline: Array<{ event: string; date: number | null }>;
    createdAt: number;
  };
}) {
  const statusLabel = STATUS_LABELS[data.status] || data.status;
  const statusColor = STATUS_COLORS[data.status] || "bg-gray-100 text-gray-800";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Package className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Seguimiento de Paquete</CardTitle>
            {data.companyName && (
              <p className="text-sm text-muted-foreground">
                {data.companyName}
              </p>
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">
                Número de Orden
              </p>
              <p className="text-2xl font-mono font-bold">{data.orderNumber}</p>
              {data.trackingNumber && (
                <p className="text-sm text-muted-foreground mt-1">
                  Tracking: {data.trackingNumber}
                </p>
              )}
            </div>

            <div className="flex justify-center">
              <span
                className={`px-4 py-2 rounded-full text-sm font-medium border ${statusColor}`}
              >
                {statusLabel}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Información de Envío
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Remitente
                </p>
                <p className="font-medium">{data.senderName || "—"}</p>
                {data.senderCity && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {data.senderCity}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Destinatario
                </p>
                <p className="font-medium">{data.recipientName || "—"}</p>
                {data.recipientCity && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {data.recipientCity}
                  </p>
                )}
              </div>
            </div>

            {(data.carrierName || data.shipmentName) && (
              <>
                <Separator />
                <div className="flex flex-wrap gap-4">
                  {data.carrierName && (
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{data.carrierName}</span>
                    </div>
                  )}
                  {data.shipmentName && (
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{data.shipmentName}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {data.timeline.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-muted" />
                <div className="space-y-4">
                  {data.timeline.map((item, index) => (
                    <div key={index} className="flex gap-4 relative">
                      <div
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                          index === data.timeline.length - 1
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {EVENT_ICONS[item.event] || (
                          <Clock className="h-3 w-3" />
                        )}
                      </div>
                      <div className="flex-1 pb-2">
                        <p className="font-medium text-sm">
                          {EVENT_LABELS[item.event] || item.event}
                        </p>
                        {item.date && (
                          <p className="text-xs text-muted-foreground">
                            {new Date(item.date).toLocaleDateString("es-HN", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-xs text-center text-muted-foreground">
          Código: <span className="font-mono">{data.code}</span>
        </p>
      </div>
    </div>
  );
}
